import { useEffect, useMemo, useRef, useState } from 'react';

import { GroupChannel } from '@sendbird/chat/groupChannel';
import { AdminMessage } from '@sendbird/chat/lib/__definition';
import {
  BaseMessage,
  FileMessage,
  UserMessage,
  MessageTypeFilter,
} from '@sendbird/chat/message';
import { DateTime } from 'luxon';

import { MESSAGES_PER_PAGE } from '~/lib/constants/chat.constants';
import urlConstants from '~/lib/constants/url.constants';
import {
  ChatMessage,
  SendBirdProxyProps,
  SendBirdValuesProxy,
} from '~/lib/types/chat';
import ChatUtil, {
  getAdminMessageText,
  getAllowedChannels,
  getNonMembershipChannels,
  getUserFromAdminMessage,
  isActiveTreatmentStatus,
  isExternalChannel,
  isReadOnlyChannel,
  removeUUIDFromFilename,
} from '~/lib/util/chat/chat.utils';
import { renderLinks, renderNewLine } from '~/lib/util/chat/chatSearch.util';

import useRemovedMessageCache from './useRemovedMessageCache';
import { useApi } from '..';
import { useGlobalContext } from '../../contexts/GlobalContext';
import { useDatadog } from '~/lib/hooks/useDatadog';

const useChatProxy = ({
  channel,
  chatUserId,
  patientId,
  universalStatusCode,
}: SendBirdProxyProps): SendBirdValuesProxy => {
  const { currentUser } = useGlobalContext();
  const { logError } = useDatadog();
  const isPatientInTreatment = isActiveTreatmentStatus(universalStatusCode);

  const [channels, updateChannels] = useState<GroupChannel[]>([]);
  const [chatMessages, updateChatMessages] = useState<ChatMessage[]>([]);
  const [hasMoreMessages, setHasMoreMessages] = useState<boolean>(true);
  const [selectedChannel, setSelectedChannel] = useState<GroupChannel>(null);
  const [sendBirdLoading, setSendBirdLoading] = useState<boolean>(false);
  const messageListRef = useRef<ChatMessage[]>([]);
  // TODO: GX-2211 - Introduce RTK
  const { sendRequest: getGroupChannelsByPatientId } = useApi<any>();
  const { sendRequest: getGroupChannelMessages } = useApi<any>();
  const { isInCache } = useRemovedMessageCache(); // readonly - can still use isRemoved cache

  // Update messageListRef whenever chatMessages change
  useEffect(() => {
    messageListRef.current = chatMessages;
  }, [chatMessages]);

  const updateMessageList = (
    messageIndex: number,
    updatedMessage: ChatMessage,
  ) => {
    const newChatMessages = [...chatMessages];
    newChatMessages.splice(messageIndex, 1, updatedMessage);
    updateChatMessages(newChatMessages);
  };

  const mapChannels = (channels: any[]): GroupChannel[] => {
    return channels.map((c) => ({
      ...c,
      isFileMessage: () =>
        c.last_message?.file && c.last_message?.type === MessageTypeFilter.FILE,
      isFrozen: c.freeze,
      members: c.members.map((m) => ({
        ...m,
        metaData: m.metadata,
        userId: m.user_id,
      })),
      url: c.channel_url,
    }));
  };

  const loadChannels = (onFailure?: () => void) => {
    setSendBirdLoading(true);
    try {
      getGroupChannelsByPatientId({
        callback: (res) => {
          const foundChannels: GroupChannel[] = mapChannels(res.channels);
          const allowedChannels = getAllowedChannels(
            foundChannels,
            isPatientInTreatment,
          );

          const nonMemberChannels = getNonMembershipChannels(
            allowedChannels,
            currentUser.userId,
          );
          if (nonMemberChannels.length > 0) {
            updateChannels(nonMemberChannels);
            setSendBirdLoading(false);
          } else {
            onFailure?.();
          }
        },
        command: 'get',
        url: urlConstants.chat.sendbirdProxy.getGroupChannelsByPatientId(
          patientId,
        ),
      });
    } catch {
      onFailure?.();
      setSendBirdLoading(false);
    }
  };

  const processNonAdminMessage = (
    newMessage: BaseMessage,
    isRead?: boolean,
  ): ChatMessage => {
    const isFileMessage = newMessage.isFileMessage();
    const {
      channelUrl,
      createdAt,
      data,
      mentionedUsers,
      messageId,
      parentMessageId,
      sender,
      threadInfo,
    } = isFileMessage
      ? (newMessage as FileMessage)
      : (newMessage as UserMessage);

    const message = isFileMessage
      ? removeUUIDFromFilename((newMessage as FileMessage).name)
      : (newMessage as UserMessage).message;

    const calculatedMentionedUserIds = (mentionedUsers ?? [])
      .map((x) => x.userId)
      .concat(sender.userId);

    const chatMessage: ChatMessage = {
      allMentionedUserIds: ChatUtil.excludeCurrentUserId(
        calculatedMentionedUserIds,
        currentUser.userId,
      ),
      channelUrl,
      createdAt,
      data,
      hasNewReply: false, // readonly - no realtime. used to be isMessageStatusInCache(messageId),
      hasThread: !!threadInfo,
      id: messageId?.toFixed(),
      isAdminMessage: false,
      isFileMessage,
      isMentioned: calculatedMentionedUserIds.includes(chatUserId),
      isMyMessage: currentUser.userId === sender?.userId,
      isNewThread: false,
      isRead:
        isRead ||
        channel?.isReadMessage(newMessage) ||
        sender.userId === chatUserId,
      isRemoved: isInCache(messageId),
      mentionedUserIds: (mentionedUsers ?? []).map((user) => user.userId),
      mentionedUsers: mentionedUsers ?? [],
      message,
      messageId,
      parentMessageId,
      relativeDate: ChatUtil.calculateRelativeDate(createdAt),
      sbMessage: newMessage,
      sender,
      thread: [],
      threadInfo,
      timestamp: DateTime.fromMillis(createdAt).toFormat('hh:mm a'),
    };

    return chatMessage;
  };

  const processAdminMessage = (
    newMessage: BaseMessage,
    isRead?: boolean,
  ): ChatMessage => {
    const {
      channelUrl,
      createdAt,
      data,
      mentionedUserIds,
      mentionedUsers,
      message,
      messageId,
      parentMessageId,
      threadInfo,
    } = newMessage as AdminMessage;

    const sender = getUserFromAdminMessage(message);

    const chatMessage: ChatMessage = {
      allMentionedUserIds: mentionedUserIds,
      channelUrl,
      createdAt,
      data,
      hasNewReply: false,
      hasThread: !!threadInfo,
      id: messageId?.toFixed(),
      isAdminMessage: true,
      isFileMessage: false,
      isMentioned: false,
      isMyMessage: false,
      isNewThread: false,
      isRead: isRead || channel?.isReadMessage(newMessage),
      isRemoved: isInCache(messageId),
      mentionedUserIds,
      mentionedUsers,
      message: getAdminMessageText(message, sender?.nickname),
      messageId,
      parentMessageId,
      relativeDate: ChatUtil.calculateRelativeDate(createdAt),
      sbMessage: newMessage,
      sender,
      thread: [],
      threadInfo,
      timestamp: DateTime.fromMillis(createdAt).toFormat('hh:mm a'),
    };

    return chatMessage;
  };

  const processMessages = (newMessages: BaseMessage[]): ChatMessage[] => {
    const processedMessages: ChatMessage[] = [];
    const isChannelReadOnly = isReadOnlyChannel(channel, currentUser.userId);

    for (const newMessage of newMessages) {
      if (!newMessage.isAdminMessage()) {
        processedMessages.push(
          processNonAdminMessage(newMessage, isChannelReadOnly),
        );
      } else {
        processedMessages.push(
          processAdminMessage(newMessage, isChannelReadOnly),
        );
      }
    }

    return renderLinks(renderNewLine(processedMessages));
  };

  const mapMessages = (
    messages: any[],
    parentMessageId: number,
  ): BaseMessage[] =>
    messages?.map((m) => ({
      ...m,
      createdAt: m.created_at,
      file: m.file,
      files: m.files,
      isAdminMessage: () => m.type === 'ADMM',
      isFileMessage: () => m.type === MessageTypeFilter.FILE,
      messageId: m.message_id,
      name: m.file?.name, // required for file names?
      parentMessageId,
      plainUrl: m.type === MessageTypeFilter.FILE && m.file.url,
      sender: { ...m.user, userId: m.user?.user_id },
      threadInfo: m.thread_info,
    }));

  const loadMessages = (
    oldestMessage?: ChatMessage | null,
    onSuccess?: (messages: ChatMessage[], hasMoreMessages: boolean) => void,
  ) => {
    try {
      getGroupChannelMessages({
        callback: (res) => {
          const messages = mapMessages(res?.messages, 0);
          const filteredMessages = messages.filter(
            (message) => message.parentMessageId === 0,
          );
          const processedMessages = processMessages(filteredMessages);

          // Use functional state update to avoid stale closures
          updateChatMessages((prevMessages) => [
            ...prevMessages,
            ...processedMessages,
          ]);

          // Determine if there are more messages to load
          const hasMore = res.messages.length === MESSAGES_PER_PAGE;
          setHasMoreMessages(hasMore);

          onSuccess?.(processedMessages, hasMore);
        },
        command: 'get',
        options: {},
        url: urlConstants.chat.sendbirdProxy.getGroupChannelMessages(
          selectedChannel.url,
          MESSAGES_PER_PAGE,
          0,
          oldestMessage ? oldestMessage.createdAt : Date.now(),
        ),
      });
    } catch (error) {
      logError(error);
    }
  };

  const addToMessageThread = (
    messageId: number,
    messages: BaseMessage[],
  ): ChatMessage => {
    const existingParentMessageIndex = ChatUtil.findMessageIndex(
      messageListRef.current ?? [],
      messageId,
    );

    const existingParentMessage =
      messageListRef.current?.[existingParentMessageIndex] ??
      ({} as ChatMessage);

    const replies = processMessages(messages);

    existingParentMessage.hasThread = true;
    existingParentMessage.thread = ChatUtil.mergeMessages(
      existingParentMessage.thread,
      replies,
    );
    existingParentMessage.hasNewReply = replies.some((reply) => !reply.isRead);
    existingParentMessage.allMentionedUserIds = ChatUtil.getAllMentionedUserIds(
      replies,
    ).concat(existingParentMessage.mentionedUserIds);

    updateMessageList(existingParentMessageIndex, existingParentMessage);

    return existingParentMessage;
  };

  const convertToThreadedMessagesObject = (
    messages: any[],
    parentMessageId: number,
  ): { parentMessage: any; threadedMessages: any[] } => {
    const parentMessage = messages.find((m) => m.messageId === parentMessageId);
    const threadedMessages = messages.filter(
      (m) => m.messageId !== parentMessage.messageId,
    );
    return { parentMessage, threadedMessages };
  };

  const getReplies = (
    message: ChatMessage,
    onSuccess?: (replies: ChatMessage[]) => void,
  ) => {
    try {
      getGroupChannelMessages({
        callback: (res) => {
          const messages = mapMessages(res?.messages, message.messageId);
          const { parentMessage, threadedMessages } =
            convertToThreadedMessagesObject(messages, message.messageId);
          if (parentMessage.messageId === message.messageId) {
            const existingParentMessage = addToMessageThread(
              parentMessage.messageId,
              threadedMessages,
            );
            onSuccess?.(existingParentMessage.thread);
          }
        },
        command: 'get',
        options: {},
        url: urlConstants.chat.sendbirdProxy.getGroupChannelMessageThread(
          selectedChannel.url,
          0,
          MESSAGES_PER_PAGE,
          message.createdAt,
          message.messageId,
        ),
      });
    } catch (error) {
      logError(error);
    } finally {
      setHasMoreMessages(false);
    }
  };

  // Reset state when selectedChannel changes
  useEffect(() => {
    if (selectedChannel) {
      // Reset state variables
      updateChatMessages([]);
      messageListRef.current = [];
      setHasMoreMessages(true);

      // Load messages for the new channel
      loadMessages(null);
    }
  }, [selectedChannel]);

  // Update selectedChannel when channel prop or channels state change
  useEffect(() => {
    if (channel) {
      setSelectedChannel(channel);
    } else if (channels && channels.length === 1) {
      setSelectedChannel(channels[0]);
    }
  }, [channel, channels]);

  return {
    channels,
    chatMessages,
    getReplies,
    hasMoreMessages,
    isChannelReadOnly: useMemo(
      () => isReadOnlyChannel(selectedChannel, currentUser.userId),
      [selectedChannel, currentUser.userId],
    ),
    isGroupChannel: useMemo(
      () => !isExternalChannel(selectedChannel?.url),
      [selectedChannel],
    ),
    loadChannels,
    loadMessages,
    sendBirdLoading,
  };
};

export default useChatProxy;
